// Import your `DataItem` type from wherever it is defined.

import { DataItem } from "./PortfolioViewData";

export interface EntityEmissionData {
  name: string;
  scope1: number;
  scope2: number;
  scope3: number;
  total: number;
}

/**
 * Aggregates raw rows into per-entity emission totals (by organization or by location).
 * Shared by EmissionByScopePieChat and EmissionByScopeCategoryBar.
 */
export function buildEntityEmissions(
  rawRows: Array<Partial<Record<string, any>>>,
  selectedScope: number[],
  isOrganizationPortfolio: boolean,
  organizationNameById: Record<number, string> = {},
): EntityEmissionData[] {
  const entityMap = new Map<string, EntityEmissionData>();

  rawRows.forEach((row) => {
    const scope1 = selectedScope.includes(0) ? Number(row?.scope1GHGEmission ?? 0) : 0;
    const scope2 = selectedScope.includes(1) ? Number(row?.scope2GHGEmission ?? 0) : 0;
    const scope3 = selectedScope.includes(2) ? Number(row?.scope3GHGEmission ?? 0) : 0;
    const total = scope1 + scope2 + scope3;

    const locationId = row?.locationID ?? row?.locationId;
    const locationName =
      row?.locationName ??
      row?.location ??
      row?.locationDisplayName ??
      (locationId != null ? `Location ${locationId}` : "Location");

    const orgId = row?.orgID ?? row?.orgId ?? row?.organizationId;
    const numericOrgId = Number(orgId);
    const selectedOrgName = Number.isFinite(numericOrgId)
      ? organizationNameById[numericOrgId]
      : undefined;
    const orgName =
      row?.organizationName ??
      row?.orgName ??
      row?.organization ??
      row?.organizationDisplayName ??
      selectedOrgName ??
      (orgId != null ? `Organization ${orgId}` : "Organization");

    const entityId = isOrganizationPortfolio ? locationId : orgId;
    const entityName = isOrganizationPortfolio ? locationName : orgName;
    const entityKey = entityId != null ? String(entityId) : entityName;

    const current = entityMap.get(entityKey) ?? {
      name: entityName,
      scope1: 0,
      scope2: 0,
      scope3: 0,
      total: 0,
    };

    current.scope1 += scope1;
    current.scope2 += scope2;
    current.scope3 += scope3;
    current.total += total;
    entityMap.set(entityKey, current);
  });

  return Array.from(entityMap.values())
    .filter((item) => item.total > 0)
    .sort((left, right) => right.total - left.total);
}

export const createDataItem = (
  name: string,
  value: number | undefined
): DataItem => ({
  name,
  value: value ?? 0,
});

/**
 * Generates and returns the data for the bar chart based on the emission data.
 *
 * @function
 * @param {any} data - The data object containing emission values.
 * @returns {Array<DataItem>} - An array of `DataItem` objects for each emission category.
 */
export const barData = (data: any): Array<DataItem> => [
  createDataItem("Purchased Goods And Services", data.purchasedGoodsAndServices),
  createDataItem("Capital Goods", data.capitalGoods),
  createDataItem("Fuel and Energy Related Activities", data.fuelandEnergyRelatedActivities),
  createDataItem("Upstream Transportation And Distribution", data.upstreamTransportationAndDistribution),
  createDataItem("Waste Generated In Operations", data.wasteGeneratedinOperations),
  createDataItem("Business Travel", data.businessTravel),
  createDataItem("Employee Commuting", data.employeeCommuting),
  createDataItem("Upstream Leased Assets", data.upstreamLeasedAssets),
  createDataItem("Downstream Transportation And Distribution", data.downstreamTransportationAndDistribution),
  createDataItem("Processing of Sold Products", data.processingofSoldProducts),
  createDataItem("Use of Sold Products", data.useofSoldProducts),
  createDataItem("End of Life Treatment of Sold Products", data.endofLifeTreatmentofSoldProducts),
  createDataItem("Downstream Leased Assets", data.downstreamLeasedAssets),
  createDataItem("Franchises", data.franchises),
  createDataItem("Investments", data.investments),
  createDataItem("Other Upstream", data.otherUpstream),
  createDataItem("Other Downstream", data.otherDownstream),
];
